// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import CrewManager from '../utils/game/CrewManager';
import CrewTrainingManager from '../utils/game/CrewTrainingManager';
import { TRAINING_PROGRAMS } from '../config/CrewTrainingConfig';
import './CrewTrainingOverview.css';

const CrewTrainingOverview = ({ embedded = false }) => {
  const [crew, setCrew] = useState([]);
  const [activeTraining, setActiveTraining] = useState([]);

  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 1000);
    return () => clearInterval(interval);
  }, []);

  const refreshData = () => {
    setCrew(CrewManager.getAllCrew());
    
    // Get all active training sessions
    const active = [];
    CrewManager.getAllCrew().forEach(member => {
      const training = CrewTrainingManager.getActiveTraining(member.id);
      if (training) {
        active.push({ ...training, crewMember: member });
      }
    });
    setActiveTraining(active);
  };

  // Count crew by training completion
  const getTrainingStats = () => {
    const stats = {};
    Object.keys(TRAINING_PROGRAMS).forEach(programId => {
      stats[programId] = {
        completed: 0,
        inProgress: 0,
        notStarted: 0
      };
    });

    crew.forEach(member => {
      const completed = CrewTrainingManager.getCompletedTraining(member.id);
      const active = CrewTrainingManager.getActiveTraining(member.id);

      Object.keys(TRAINING_PROGRAMS).forEach(programId => {
        const hasCompleted = completed.some(t => t.programId === programId.toLowerCase());
        const isActive = active && active.programId === programId.toLowerCase();

        if (hasCompleted) {
          stats[programId].completed++;
        } else if (isActive) {
          stats[programId].inProgress++;
        } else {
          stats[programId].notStarted++;
        }
      });
    });

    return stats;
  };

  const trainingStats = getTrainingStats();

  return (
    <div className={`crew-training-overview ${embedded ? 'embedded' : ''}`}>
      <div className="academy-header">
        <h3>🎓 STARSHIP ACADEMY</h3>
        <p className="academy-motto">"Excellence Through Training"</p>
        <div className="academy-stats">
          <div className="academy-stat">
            <span className="stat-number">{crew.length}</span>
            <span className="stat-label">Cadets</span>
          </div>
          <div className="academy-stat">
            <span className="stat-number">{activeTraining.length}</span>
            <span className="stat-label">In Training</span>
          </div>
          <div className="academy-stat">
            <span className="stat-number">
              {crew.reduce((sum, m) => sum + (CrewTrainingManager.getCompletedTraining(m.id).length || 0), 0)}
            </span>
            <span className="stat-label">Certifications</span>
          </div>
        </div>
      </div>

      {/* Current Classes */}
      {activeTraining.length > 0 && (
        <div className="academy-section">
          <h4>📚 CURRENT CLASSES</h4>
          <div className="class-roster">
            {activeTraining.map(session => (
              <div key={session.crewId} className="class-card">
                <div className="class-header">
                  <span className="class-icon">{session.program.icon}</span>
                  <div className="class-info">
                    <div className="class-name">{session.program.name}</div>
                    <div className="class-instructor">Instructor: Academy Staff</div>
                  </div>
                </div>
                <div className="class-student">
                  <span className="student-icon">{session.crewMember.roleData.icon}</span>
                  <span className="student-name">{session.crewMember.name}</span>
                  <span className="student-rank">{session.crewMember.experience}</span>
                </div>
                <div className="class-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${session.progress * 100}%` }}
                    />
                  </div>
                  <span className="progress-label">
                    Day {Math.floor(session.progress * session.program.duration)} of {session.program.duration}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Course Catalog */}
      <div className="academy-section">
        <h4>📖 COURSE CATALOG</h4>
        <div className="course-catalog">
          {Object.entries(TRAINING_PROGRAMS).map(([programId, program]) => {
            const stats = trainingStats[programId];
            const total = crew.length;
            const graduationRate = total > 0 ? Math.round((stats.completed / total) * 100) : 0;

            return (
              <div key={programId} className="course-card">
                <div className="course-header">
                  <span className="course-icon">{program.icon}</span>
                  <div className="course-title">
                    <div className="course-name">{program.name}</div>
                    <div className="course-code">ACAD-{programId.substring(0, 3).toUpperCase()}-{program.duration}D</div>
                  </div>
                </div>
                <div className="course-description">{program.description}</div>
                <div className="course-details">
                  <div className="detail-item">
                    <span className="detail-icon">⏱️</span>
                    <span>{program.duration} days</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">💰</span>
                    <span>{program.cost} CR</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">⭐</span>
                    <span>+{program.rewards.xp} XP</span>
                  </div>
                </div>
                <div className="course-enrollment">
                  <div className="enrollment-stats">
                    <div className="enrollment-item graduated">
                      <span className="enrollment-icon">🎓</span>
                      <span>{stats.completed} Graduated</span>
                    </div>
                    <div className="enrollment-item enrolled">
                      <span className="enrollment-icon">📝</span>
                      <span>{stats.inProgress} Enrolled</span>
                    </div>
                    <div className="enrollment-item available">
                      <span className="enrollment-icon">○</span>
                      <span>{stats.notStarted} Available</span>
                    </div>
                  </div>
                  <div className="graduation-rate">
                    <div className="rate-bar">
                      <div 
                        className="rate-fill"
                        style={{ width: `${graduationRate}%` }}
                      />
                    </div>
                    <span className="rate-text">{graduationRate}% Graduation Rate</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeTraining.length === 0 && crew.length > 0 && (
        <div className="academy-notice">
          <p>📢 No cadets currently enrolled in courses.</p>
          <p>Visit the Roster tab to enroll crew members in training programs.</p>
        </div>
      )}
    </div>
  );
};

export default CrewTrainingOverview;
